import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { IconUserPlus } from '@tabler/icons';

import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/toast';
import { useOnlineStatus } from '../../../hooks/onlineStatus';
import api from '../../../services/api';

import BackHeader from '../../../components/BackHeader';
import UserItem from '../../../components/UserItem';
import SearchInput from '../../../components/Form/SearchInput';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import Offline from '../../../components/OfflineMessage';
import { Label, H2, Skeleton, Spin, Row } from '../../../components/Generic';
import { Container, Content } from './styles';

const Contributors: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [property, setProperty] = useState<any>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [requests, setRequests] = useState<any[]>([]);
  const [searchedUsers, setSearchedUsers] = useState<any>([]);
  const [query, setQuery] = useState('');

  const { user } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();
  const isOnline = useOnlineStatus();
  const { id }: any = useParams();

  useEffect(() => {
    getProperty();
  }, []);

  const getProperty = async () => {
    try {
      setLoading(true);

      const response = await api.get(`/propriedades/`, {
        headers: {
          propriedade_id: id,
        },
      });
      setProperty(response.data);

      const reqResponse = await api.get(`/pedidos-de-colaboracao/`, {
        headers: {
          propriedade_id: id,
        },
      });

      setRequests(reqResponse.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(
    async (key: string) => {
      try {
        setActionLoading(true);

        const response = await api.get(
          `/pesquisas/usuarios?limite=10&pagina=1&key=${key}`,
        );

        setSearchedUsers(response.data.usuarios);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: 'Algo deu errado.',
        });
      } finally {
        setActionLoading(false);
      }
    },
    [addToast],
  );

  const approveUser = useCallback(
    async (user_id: string, collab_id: string) => {
      try {
        await api.put(
          `/pedidos-de-colaboracao/${collab_id}`,
          {
            propriedade_id: id,
            usuario_id: user_id,
            aceito: true,
          },
          {
            headers: {
              propriedade_id: id,
            },
          },
        );

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Usuário adicionado.',
        });

        getProperty();
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado.',
        });
      }
    },
    [useToast],
  );

  const disapproveUser = useCallback(
    async (user_id: string, collab_id: string) => {
      try {
        await api.put(
          `/pedidos-de-colaboracao/${collab_id}`,
          {
            propriedade_id: id,
            usuario_id: user_id,
            status: false,
          },
          {
            headers: {
              propriedade_id: id,
            },
          },
        );

        addToast({
          type: 'success',
          title: 'Removido!',
          description: 'Usuário removido.',
        });

        getProperty();
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado.',
        });
      }
    },
    [useToast],
  );

  const removeUser = useCallback(
    async (collab_id: string) => {
      try {
        if (
          !window.confirm('Tem certeza de que quer remover este colaborador?')
        ) {
          return;
        }

        await api.delete(`/colaboradores/${collab_id}`, {
          headers: {
            propriedade_id: id,
          },
        });

        addToast({
          type: 'success',
          title: 'Removido!',
          description: 'Usuário removido.',
        });

        getProperty();
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado.',
        });
      }
    },
    [useToast],
  );

  const addUser = useCallback(
    async (user_id: string) => {
      try {
        setActionLoading(true);

        await api.post(
          `/colaboradores/`,
          {
            usuario_id: user_id,
          },
          {
            headers: {
              propriedade_id: id,
            },
          },
        );

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Usuário adicionado como colaborador.',
        });

        getProperty();
        setIsModalOpen(false);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado!',
        });
      } finally {
        setActionLoading(false);
      }
    },
    [useToast],
  );

  return (
    <Container>
      <BackHeader title="Colaboradores" />

      {!isOnline ? (
        <Offline />
      ) : loading ? (
        <Content>
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 30 }} />
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 30 }} />
          <Skeleton style={{ height: 30, width: 100, marginBottom: 20 }} />
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 30 }} />
        </Content>
      ) : (
        <Content>
          <Button
            onClick={() => setIsModalOpen(true)}
            style={{ marginBottom: 40 }}
          >
            <IconUserPlus size={24} />
            Adicionar colaborador
          </Button>

          <UserItem
            avatar_url={user.url}
            name={user.nome}
            description={user.email}
            owner
          />

          {property.colaboradores?.map((item: any, index: any) => (
            <UserItem
              key={index}
              name={item.usuarios?.nome}
              avatar_url={item.usuarios?.url}
              description={item.usuarios?.email}
              onDisapprove={() => removeUser(property.colaboradores[index].id)}
              onClick={() => history.push(`/usuario/${item.usuario_id}`)}
              canRemove
            />
          ))}

          {requests?.length > 0 && <Label>Solicitações</Label>}

          {requests?.map((item: any) => (
            <UserItem
              key={item.usuario_id}
              avatar_url={item.usuarios?.url}
              name={item.usuarios?.nome}
              description={item.usuarios?.email}
              needApproval={true}
              onApprove={() => approveUser(item.usuario_id, item.id)}
              onDisapprove={() => disapproveUser(item.usuario_id, item.id)}
              onClick={() => history.push(`/usuario/${item.usuario_id}`)}
            />
          ))}

          <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <H2>Pesquisar usuário</H2>

            <SearchInput
              placeholder="Digite o email ou o nome"
              onChange={(e: any) => setQuery(e.target.value)}
              onSubmit={() => handleSearch(query)}
              style={{ marginBottom: 20 }}
              isLoading={actionLoading}
            />

            {searchedUsers?.map((item: any) => (
              <UserItem
                avatar_url={item.url}
                name={item.nome}
                description={item.email}
                style={{ marginTop: 20, cursor: 'pointer' }}
                onClick={() => addUser(item.id)}
              />
            ))}
          </Modal>
        </Content>
      )}
    </Container>
  );
};

export default Contributors;
